import { ReactNode } from 'react'
import {
  Play,
  HelpCircle,
  Lightbulb,
  BookOpen,
  Quote,
  Check,
  X,
  Cpu,
  Users,
  Flag,
  MessageSquare,
  Clock,
  ChefHat,
  ListOrdered,
  Replace,
  Refrigerator,
  GlassWater,
  StickyNote,
  Target,
  Wrench,
  AlertTriangle,
  Microscope,
  FlaskConical,
  HelpingHand,
  User,
  Compass,
  Newspaper,
  Globe,
  Link as LinkIcon,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type {
  VideoDetail,
  LectureExtras,
  ExplainerExtras,
  TechReviewExtras,
  DebateOrPanelExtras,
  CookingExtras,
  CookingIngredient,
  TutorialExtras,
  ResearchTalkExtras,
  InterviewExtras,
  NewsAnalysisExtras,
  NewsBriefExtras,
} from '@/lib/api'

// ---------------------------------------------------------------------
// Shared helpers — kept inline rather than extracted to a util module
// since they're only used by these components today. Pull them out if
// a third caller appears.
// ---------------------------------------------------------------------

function formatTimestamp(seconds: number): string {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  return `${m}:${s.toString().padStart(2, '0')}`
}

function youtubeTimestampUrl(videoUrl: string, seconds: number): string {
  const base = videoUrl.split('&')[0]
  return `${base}&t=${seconds}s`
}

function TimestampPill({ url, seconds }: { url: string; seconds: number }) {
  if (!seconds || seconds <= 0) return null
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-mono font-medium text-primary transition-colors hover:bg-primary/20"
    >
      <Play className="h-3 w-3" />
      {formatTimestamp(seconds)}
    </a>
  )
}

function ExtrasCard({ title, icon: Icon, children }: { title: string; icon: typeof Play; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-primary" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------
// Top-level dispatch. Returns null when content_type isn't a Phase 1
// type or structured_extras is missing. The caller (video.tsx) just
// drops <StructuredExtras /> into the page; v1 summaries become a no-op.
// ---------------------------------------------------------------------

export function StructuredExtras({ video }: { video: VideoDetail }) {
  const extras = video.structured_extras
  if (!extras || !video.content_type) return null

  switch (video.content_type) {
    case 'lecture':
      return <LectureBlock video={video} extras={extras as LectureExtras} />
    case 'explainer':
      return <ExplainerBlock video={video} extras={extras as ExplainerExtras} />
    case 'tech_review':
      return <TechReviewBlock extras={extras as TechReviewExtras} videoUrl={video.youtube_url} />
    case 'debate_or_panel':
      return <DebateOrPanelBlock video={video} extras={extras as DebateOrPanelExtras} />
    case 'cooking':
      return <CookingBlock video={video} extras={extras as CookingExtras} />
    case 'tutorial':
      return <TutorialBlock video={video} extras={extras as TutorialExtras} />
    case 'research_talk':
      return <ResearchTalkBlock video={video} extras={extras as ResearchTalkExtras} />
    case 'interview':
      return <InterviewBlock video={video} extras={extras as InterviewExtras} />
    case 'news_analysis':
      return <NewsAnalysisBlock video={video} extras={extras as NewsAnalysisExtras} />
    case 'news_brief':
      return <NewsBriefBlock video={video} extras={extras as NewsBriefExtras} />
    // entertainment has no extras by design — v1 layout only.
    default:
      return null
  }
}

// ---------------------------------------------------------------------
// LECTURE
// ---------------------------------------------------------------------

function LectureBlock({ video, extras }: { video: VideoDetail; extras: LectureExtras }) {
  const hasAny =
    extras.concepts_introduced?.length > 0 ||
    extras.prerequisites?.length > 0 ||
    extras.references?.length > 0 ||
    extras.key_quotes?.length > 0
  if (!hasAny) return null

  return (
    <>
      {extras.concepts_introduced?.length > 0 && (
        <ExtrasCard title="Concepts introduced" icon={Lightbulb}>
          <ul className="space-y-3">
            {extras.concepts_introduced.map((c, i) => (
              <li key={i} className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">{c.term}</span>
                  <TimestampPill url={youtubeTimestampUrl(video.youtube_url, c.timestamp_seconds)} seconds={c.timestamp_seconds} />
                </div>
                {c.plain_definition && (
                  <p className="text-sm leading-relaxed text-foreground/80">{c.plain_definition}</p>
                )}
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.prerequisites?.length > 0 && (
        <ExtrasCard title="Prerequisites" icon={BookOpen}>
          <ul className="flex flex-wrap gap-2">
            {extras.prerequisites.map((p, i) => (
              <li key={i}>
                <Badge variant="secondary" className="font-normal">{p}</Badge>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.references?.length > 0 && (
        <ExtrasCard title="References" icon={BookOpen}>
          <ul className="space-y-2.5">
            {extras.references.map((r, i) => (
              <li key={i} className="flex flex-wrap items-start gap-2">
                <Badge variant="outline" className="font-normal capitalize">{r.kind}</Badge>
                <span className="flex-1 text-sm text-foreground/80">{r.title}</span>
                <TimestampPill url={youtubeTimestampUrl(video.youtube_url, r.timestamp_seconds)} seconds={r.timestamp_seconds} />
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.key_quotes?.length > 0 && (
        <ExtrasCard title="Key quotes" icon={Quote}>
          <ul className="space-y-3">
            {extras.key_quotes.map((q, i) => (
              <li key={i} className="border-l-2 border-primary/40 pl-3">
                <p className="text-sm italic leading-relaxed text-foreground/85">"{q.quote}"</p>
                <div className="mt-1.5">
                  <TimestampPill url={youtubeTimestampUrl(video.youtube_url, q.timestamp_seconds)} seconds={q.timestamp_seconds} />
                </div>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}
    </>
  )
}

// ---------------------------------------------------------------------
// EXPLAINER
// ---------------------------------------------------------------------

function ExplainerBlock({ video, extras }: { video: VideoDetail; extras: ExplainerExtras }) {
  return (
    <>
      {extras.central_question && (
        <ExtrasCard title="The question" icon={HelpCircle}>
          <p className="text-base font-medium leading-relaxed text-foreground">{extras.central_question}</p>
        </ExtrasCard>
      )}

      {extras.concepts_introduced?.length > 0 && (
        <ExtrasCard title="Concepts introduced" icon={Lightbulb}>
          <ul className="space-y-3">
            {extras.concepts_introduced.map((c, i) => (
              <li key={i} className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">{c.term}</span>
                  <TimestampPill url={youtubeTimestampUrl(video.youtube_url, c.timestamp_seconds)} seconds={c.timestamp_seconds} />
                </div>
                {c.plain_definition && (
                  <p className="text-sm leading-relaxed text-foreground/80">{c.plain_definition}</p>
                )}
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.takeaways?.length > 0 && (
        <ExtrasCard title="Takeaways" icon={Lightbulb}>
          <ul className="space-y-2">
            {extras.takeaways.map((t, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" aria-hidden="true" />
                <span className="text-sm leading-relaxed text-foreground/85">{t}</span>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.references?.length > 0 && (
        <ExtrasCard title="References" icon={BookOpen}>
          <ul className="space-y-2.5">
            {extras.references.map((r, i) => (
              <li key={i} className="flex flex-wrap items-start gap-2">
                <Badge variant="outline" className="font-normal capitalize">{r.kind}</Badge>
                <span className="flex-1 text-sm text-foreground/80">{r.title}</span>
                <TimestampPill url={youtubeTimestampUrl(video.youtube_url, r.timestamp_seconds)} seconds={r.timestamp_seconds} />
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}
    </>
  )
}

// ---------------------------------------------------------------------
// TECH REVIEW
// ---------------------------------------------------------------------

const RECOMMENDATION_LABEL: Record<TechReviewExtras['recommendation'], { label: string; tone: 'good' | 'wait' | 'skip' | 'neutral' }> = {
  buy: { label: 'Buy', tone: 'good' },
  wait: { label: 'Wait', tone: 'wait' },
  skip: { label: 'Skip', tone: 'skip' },
  depends: { label: 'Depends', tone: 'neutral' },
  no_clear_recommendation: { label: 'No clear recommendation', tone: 'neutral' },
}

function TechReviewBlock({ extras, videoUrl }: { extras: TechReviewExtras; videoUrl: string }) {
  const rec = RECOMMENDATION_LABEL[extras.recommendation] ?? RECOMMENDATION_LABEL.no_clear_recommendation
  const toneClass: Record<typeof rec.tone, string> = {
    good: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    wait: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    skip: 'bg-destructive/15 text-destructive border-destructive/30',
    neutral: 'bg-muted text-muted-foreground border-border',
  }

  return (
    <>
      <ExtrasCard title="Verdict" icon={Cpu}>
        {extras.product?.name && (
          <div className="space-y-1">
            <div className="text-lg font-semibold text-foreground">{extras.product.name}</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {extras.product.category && <span className="capitalize">{extras.product.category}</span>}
              {extras.product.price_mentioned && (
                <>
                  <span>·</span>
                  <span>{extras.product.price_mentioned}</span>
                </>
              )}
            </div>
          </div>
        )}
        {extras.verdict && (
          <p className="text-sm leading-relaxed text-foreground/85">{extras.verdict}</p>
        )}
        <div className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${toneClass[rec.tone]}`}>
          {rec.label}
        </div>
      </ExtrasCard>

      {(extras.pros?.length > 0 || extras.cons?.length > 0) && (
        <div className="grid gap-4 md:grid-cols-2">
          {extras.pros?.length > 0 && (
            <ExtrasCard title="Pros" icon={Check}>
              <ul className="space-y-2">
                {extras.pros.map((p, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                    <span className="flex-1 text-sm leading-snug text-foreground/85">{p.text}</span>
                    <TimestampPill url={youtubeTimestampUrl(videoUrl, p.timestamp_seconds)} seconds={p.timestamp_seconds} />
                  </li>
                ))}
              </ul>
            </ExtrasCard>
          )}
          {extras.cons?.length > 0 && (
            <ExtrasCard title="Cons" icon={X}>
              <ul className="space-y-2">
                {extras.cons.map((c, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <span className="flex-1 text-sm leading-snug text-foreground/85">{c.text}</span>
                    <TimestampPill url={youtubeTimestampUrl(videoUrl, c.timestamp_seconds)} seconds={c.timestamp_seconds} />
                  </li>
                ))}
              </ul>
            </ExtrasCard>
          )}
        </div>
      )}

      {extras.specs?.length > 0 && (
        <ExtrasCard title="Specs" icon={Cpu}>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <tbody>
                {extras.specs.map((s, i) => (
                  <tr key={i} className="border-t border-border first:border-t-0">
                    <td className="py-2 pl-2 pr-3 align-top text-xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                      {s.attribute}
                    </td>
                    <td className="py-2 pr-2 align-top text-sm text-foreground/85">
                      {s.value}
                    </td>
                    <td className="py-2 pr-2 align-top text-right">
                      <TimestampPill url={youtubeTimestampUrl(videoUrl, s.timestamp_seconds)} seconds={s.timestamp_seconds} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ExtrasCard>
      )}

      {extras.compared_to?.length > 0 && (
        <ExtrasCard title="Compared to" icon={Cpu}>
          <ul className="space-y-3">
            {extras.compared_to.map((c, i) => (
              <li key={i} className="space-y-1">
                <div className="text-sm font-semibold text-foreground">{c.name}</div>
                <p className="text-sm leading-relaxed text-foreground/80">{c.verdict_vs}</p>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {(extras.best_for?.length > 0 || extras.not_for?.length > 0) && (
        <div className="grid gap-4 md:grid-cols-2">
          {extras.best_for?.length > 0 && (
            <ExtrasCard title="Best for" icon={Check}>
              <ul className="flex flex-wrap gap-2">
                {extras.best_for.map((p, i) => (
                  <li key={i}>
                    <Badge variant="secondary" className="font-normal">{p}</Badge>
                  </li>
                ))}
              </ul>
            </ExtrasCard>
          )}
          {extras.not_for?.length > 0 && (
            <ExtrasCard title="Not for" icon={X}>
              <ul className="flex flex-wrap gap-2">
                {extras.not_for.map((p, i) => (
                  <li key={i}>
                    <Badge variant="outline" className="font-normal">{p}</Badge>
                  </li>
                ))}
              </ul>
            </ExtrasCard>
          )}
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------
// DEBATE / PANEL
// ---------------------------------------------------------------------

function DebateOrPanelBlock({ video, extras }: { video: VideoDetail; extras: DebateOrPanelExtras }) {
  return (
    <>
      {extras.participants?.length > 0 && (
        <ExtrasCard title="Participants" icon={Users}>
          <ul className="flex flex-wrap gap-2">
            {extras.participants.map((p, i) => (
              <li key={i}>
                <Badge variant="secondary" className="font-normal">{p}</Badge>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.topics_in_order?.length > 0 && (
        <ExtrasCard title="Chapters" icon={ListOrdered}>
          <ul className="space-y-2">
            {extras.topics_in_order.map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <TimestampPill url={youtubeTimestampUrl(video.youtube_url, t.start_seconds)} seconds={t.start_seconds} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">{t.topic}</div>
                  {t.end_seconds > t.start_seconds && (
                    <div className="text-xs text-muted-foreground">
                      {formatTimestamp(t.start_seconds)} – {formatTimestamp(t.end_seconds)}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.positions?.length > 0 && (
        <ExtrasCard title="Positions" icon={Flag}>
          <ul className="space-y-3">
            {extras.positions.map((p, i) => (
              <li key={i} className="space-y-1 border-l-2 border-primary/40 pl-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">{p.speaker}</span>
                  {p.on_topic && (
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">on {p.on_topic}</span>
                  )}
                  <TimestampPill url={youtubeTimestampUrl(video.youtube_url, p.timestamp_seconds)} seconds={p.timestamp_seconds} />
                </div>
                <p className="text-sm leading-relaxed text-foreground/85">{p.position}</p>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.cruxes?.length > 0 && (
        <ExtrasCard title="Real disagreements" icon={Flag}>
          <ul className="space-y-3">
            {extras.cruxes.map((c, i) => (
              <li key={i} className="flex items-start gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <Flag className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <div className="flex-1 space-y-1">
                  <p className="text-sm leading-relaxed text-foreground/85">{c.description}</p>
                  <TimestampPill url={youtubeTimestampUrl(video.youtube_url, c.timestamp_seconds)} seconds={c.timestamp_seconds} />
                </div>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.notable_quotes?.length > 0 && (
        <ExtrasCard title="Notable quotes" icon={MessageSquare}>
          <ul className="space-y-3">
            {extras.notable_quotes.map((q, i) => (
              <li key={i} className="border-l-2 border-primary/40 pl-3">
                <p className="text-sm italic leading-relaxed text-foreground/85">"{q.quote}"</p>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">— {q.speaker}</span>
                  <TimestampPill url={youtubeTimestampUrl(video.youtube_url, q.timestamp_seconds)} seconds={q.timestamp_seconds} />
                </div>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}
    </>
  )
}

// ---------------------------------------------------------------------
// COOKING — full structured recipe.
// ---------------------------------------------------------------------

function groupIngredients(ingredients: CookingIngredient[]): Map<string | null, CookingIngredient[]> {
  const groups = new Map<string | null, CookingIngredient[]>()
  for (const i of ingredients) {
    const key = i.group?.trim() || null
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(i)
  }
  return groups
}

function ingredientLabel(i: CookingIngredient): string {
  const qty = [i.quantity, i.unit].filter(Boolean).join(' ').trim()
  const base = qty ? `${qty} ${i.name}` : i.name
  return i.preparation ? `${base}, ${i.preparation}` : base
}

function CookingBlock({ video, extras }: { video: VideoDetail; extras: CookingExtras }) {
  const r = extras.recipe
  if (!r) return null

  const metaParts: string[] = []
  if (r.servings !== undefined) metaParts.push(`${r.servings} serving${String(r.servings) === '1' ? '' : 's'}`)
  if (r.total_time_minutes) metaParts.push(`${r.total_time_minutes} min total`)
  else {
    const pieces: string[] = []
    if (r.prep_time_minutes) pieces.push(`${r.prep_time_minutes} prep`)
    if (r.cook_time_minutes) pieces.push(`${r.cook_time_minutes} cook`)
    if (pieces.length > 0) metaParts.push(pieces.join(' + '))
  }
  if (r.cuisine) metaParts.push(r.cuisine)
  if (r.difficulty) metaParts.push(r.difficulty)

  const groups = groupIngredients(r.ingredients ?? [])

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ChefHat className="h-4 w-4 text-primary" />
            Recipe
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <div className="text-xl font-bold text-foreground">{r.name || 'Recipe'}</div>
            {metaParts.length > 0 && (
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                {metaParts.map((p, i) => (
                  <span key={i} className="capitalize">{p}</span>
                )).reduce<ReactNode[]>((acc, el, i) => {
                  if (i > 0) acc.push(<span key={`sep-${i}`} aria-hidden="true">·</span>)
                  acc.push(el)
                  return acc
                }, [])}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {r.ingredients?.length > 0 && (
        <ExtrasCard title="Ingredients" icon={ListOrdered}>
          {Array.from(groups.entries()).map(([groupName, items]) => (
            <div key={groupName ?? '__no_group__'} className="space-y-2">
              {groupName && (
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{groupName}</div>
              )}
              <ul className="space-y-1.5">
                {items.map((ing, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" aria-hidden="true" />
                    <span className="text-sm leading-snug text-foreground/85">{ingredientLabel(ing)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </ExtrasCard>
      )}

      {r.equipment?.length > 0 && (
        <ExtrasCard title="Equipment" icon={ChefHat}>
          <ul className="flex flex-wrap gap-2">
            {r.equipment.map((e, i) => (
              <li key={i}>
                <Badge variant="outline" className="font-normal">{e}</Badge>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {r.steps?.length > 0 && (
        <ExtrasCard title="Steps" icon={ListOrdered}>
          <ol className="space-y-4">
            {r.steps.map((step) => (
              <li key={step.n} className="flex gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {step.n}
                </div>
                <div className="flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-start gap-2">
                    <span className="flex-1 text-sm leading-relaxed text-foreground/85">{step.instruction}</span>
                    <TimestampPill url={youtubeTimestampUrl(video.youtube_url, step.timestamp_seconds)} seconds={step.timestamp_seconds} />
                  </div>
                  {step.duration_seconds_in_video !== undefined && step.duration_seconds_in_video > 0 && (
                    <div className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatCookDuration(step.duration_seconds_in_video)}
                    </div>
                  )}
                  {step.tip && (
                    <p className="rounded-md border border-amber-500/20 bg-amber-500/5 px-2.5 py-1.5 text-xs leading-relaxed text-amber-300/90">
                      <span className="font-semibold">Tip: </span>
                      {step.tip}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </ExtrasCard>
      )}

      {r.finishing?.length > 0 && (
        <ExtrasCard title="Finishing" icon={ChefHat}>
          <ul className="space-y-2">
            {r.finishing.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" aria-hidden="true" />
                <span className="text-sm leading-relaxed text-foreground/85">{f}</span>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {r.substitutions?.length > 0 && (
        <ExtrasCard title="Substitutions" icon={Replace}>
          <ul className="space-y-2.5">
            {r.substitutions.map((s, i) => (
              <li key={i} className="text-sm">
                <span className="text-foreground/60 line-through">{s.original}</span>
                <span className="mx-2 text-muted-foreground">→</span>
                <span className="font-medium text-foreground">{s.substitute}</span>
                {s.note && <span className="ml-2 text-xs text-muted-foreground">({s.note})</span>}
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {(r.storage || (r.pairings?.length ?? 0) > 0) && (
        <div className="grid gap-4 md:grid-cols-2">
          {r.storage && (
            <ExtrasCard title="Storage" icon={Refrigerator}>
              <p className="text-sm leading-relaxed text-foreground/85">{r.storage}</p>
            </ExtrasCard>
          )}
          {(r.pairings?.length ?? 0) > 0 && (
            <ExtrasCard title="Pairings" icon={GlassWater}>
              <ul className="flex flex-wrap gap-2">
                {r.pairings!.map((p, i) => (
                  <li key={i}>
                    <Badge variant="secondary" className="font-normal">{p}</Badge>
                  </li>
                ))}
              </ul>
            </ExtrasCard>
          )}
        </div>
      )}

      {(extras.notes_from_creator?.length ?? 0) > 0 && (
        <ExtrasCard title="Notes from the creator" icon={StickyNote}>
          <ul className="space-y-2">
            {extras.notes_from_creator!.map((n, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50" aria-hidden="true" />
                <span className="text-sm leading-relaxed text-foreground/80">{n}</span>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}
    </>
  )
}

function formatCookDuration(seconds: number): string {
  const mins = Math.round(seconds / 60)
  if (mins < 60) return `${mins} min`
  const hours = Math.floor(mins / 60)
  const rem = mins % 60
  return rem === 0 ? `${hours} h` : `${hours} h ${rem} min`
}

// ---------------------------------------------------------------------
// TUTORIAL
// ---------------------------------------------------------------------

const TUTORIAL_DIFFICULTY_TONE: Record<TutorialExtras['difficulty'], string> = {
  beginner: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  intermediate: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  advanced: 'bg-destructive/15 text-destructive border-destructive/30',
}

function TutorialBlock({ video, extras }: { video: VideoDetail; extras: TutorialExtras }) {
  return (
    <>
      <ExtrasCard title="What you'll build" icon={Target}>
        {extras.goal && (
          <p className="text-sm leading-relaxed text-foreground/85">{extras.goal}</p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${TUTORIAL_DIFFICULTY_TONE[extras.difficulty]}`}>
            {extras.difficulty}
          </span>
          {typeof extras.estimated_time_minutes === 'number' && extras.estimated_time_minutes > 0 && (
            <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2.5 py-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              ≈{extras.estimated_time_minutes} min to follow along
            </span>
          )}
        </div>
        {extras.outcome && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground/85">Outcome: </span>
            {extras.outcome}
          </p>
        )}
      </ExtrasCard>

      {(extras.prerequisites?.length > 0 || extras.dependencies?.length > 0) && (
        <div className="grid gap-4 md:grid-cols-2">
          {extras.prerequisites?.length > 0 && (
            <ExtrasCard title="Prerequisites" icon={BookOpen}>
              <ul className="flex flex-wrap gap-2">
                {extras.prerequisites.map((p, i) => (
                  <li key={i}>
                    <Badge variant="secondary" className="font-normal">{p}</Badge>
                  </li>
                ))}
              </ul>
            </ExtrasCard>
          )}
          {extras.dependencies?.length > 0 && (
            <ExtrasCard title="Dependencies" icon={Wrench}>
              <ul className="flex flex-wrap gap-2">
                {extras.dependencies.map((d, i) => (
                  <li key={i}>
                    <Badge variant="outline" className="font-normal">{d}</Badge>
                  </li>
                ))}
              </ul>
            </ExtrasCard>
          )}
        </div>
      )}

      {extras.steps?.length > 0 && (
        <ExtrasCard title="Steps" icon={ListOrdered}>
          <ol className="space-y-4">
            {extras.steps.map((step) => (
              <li key={step.n} className="flex gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {step.n}
                </div>
                <div className="flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-start gap-2">
                    <span className="flex-1 text-sm font-medium leading-relaxed text-foreground/90">{step.title}</span>
                    <TimestampPill url={youtubeTimestampUrl(video.youtube_url, step.timestamp_seconds)} seconds={step.timestamp_seconds} />
                  </div>
                  {step.detail && (
                    <p className="text-sm leading-relaxed text-foreground/75">{step.detail}</p>
                  )}
                  {step.gotcha && (
                    <p className="flex items-start gap-1.5 rounded-md border border-amber-500/20 bg-amber-500/5 px-2.5 py-1.5 text-xs leading-relaxed text-amber-300/90">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      <span><span className="font-semibold">Gotcha: </span>{step.gotcha}</span>
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </ExtrasCard>
      )}
    </>
  )
}

// ---------------------------------------------------------------------
// RESEARCH TALK
// ---------------------------------------------------------------------

function ResearchTalkBlock({ video, extras }: { video: VideoDetail; extras: ResearchTalkExtras }) {
  return (
    <>
      {extras.research_question && (
        <ExtrasCard title="Research question" icon={Microscope}>
          <p className="text-base font-medium leading-relaxed text-foreground">{extras.research_question}</p>
        </ExtrasCard>
      )}

      {extras.methods?.length > 0 && (
        <ExtrasCard title="Methods" icon={FlaskConical}>
          <ul className="space-y-2">
            {extras.methods.map((m, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" aria-hidden="true" />
                <span className="text-sm leading-relaxed text-foreground/85">{m}</span>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.findings?.length > 0 && (
        <ExtrasCard title="Findings" icon={Lightbulb}>
          <ul className="space-y-2.5">
            {extras.findings.map((f, i) => (
              <li key={i} className="flex flex-wrap items-start gap-2">
                <span className="flex-1 text-sm leading-relaxed text-foreground/85">{f.text}</span>
                <TimestampPill url={youtubeTimestampUrl(video.youtube_url, f.timestamp_seconds)} seconds={f.timestamp_seconds} />
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.references?.length > 0 && (
        <ExtrasCard title="References" icon={BookOpen}>
          <ul className="space-y-2.5">
            {extras.references.map((r, i) => (
              <li key={i} className="flex flex-wrap items-start gap-2">
                <Badge variant="outline" className="font-normal capitalize">{r.kind}</Badge>
                <span className="flex-1 text-sm text-foreground/80">{r.title}</span>
                <TimestampPill url={youtubeTimestampUrl(video.youtube_url, r.timestamp_seconds)} seconds={r.timestamp_seconds} />
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.open_questions?.length > 0 && (
        <ExtrasCard title="Open questions" icon={HelpCircle}>
          <ul className="space-y-2">
            {extras.open_questions.map((q, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-sm leading-relaxed text-foreground/85">{q}</span>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.key_quotes?.length > 0 && (
        <ExtrasCard title="Key quotes" icon={Quote}>
          <ul className="space-y-3">
            {extras.key_quotes.map((q, i) => (
              <li key={i} className="border-l-2 border-primary/40 pl-3">
                <p className="text-sm italic leading-relaxed text-foreground/85">"{q.quote}"</p>
                <div className="mt-1.5">
                  <TimestampPill url={youtubeTimestampUrl(video.youtube_url, q.timestamp_seconds)} seconds={q.timestamp_seconds} />
                </div>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}
    </>
  )
}

// ---------------------------------------------------------------------
// INTERVIEW
// ---------------------------------------------------------------------

function InterviewBlock({ video, extras }: { video: VideoDetail; extras: InterviewExtras }) {
  return (
    <>
      {(extras.guest?.name || extras.guest?.background) && (
        <ExtrasCard title="Guest" icon={User}>
          {extras.guest.name && (
            <div className="text-lg font-semibold text-foreground">{extras.guest.name}</div>
          )}
          {extras.guest.background && (
            <p className="text-sm leading-relaxed text-muted-foreground">{extras.guest.background}</p>
          )}
        </ExtrasCard>
      )}

      {extras.topics_in_order?.length > 0 && (
        <ExtrasCard title="Chapters" icon={ListOrdered}>
          <ul className="space-y-2">
            {extras.topics_in_order.map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <TimestampPill url={youtubeTimestampUrl(video.youtube_url, t.start_seconds)} seconds={t.start_seconds} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">{t.topic}</div>
                  {t.end_seconds > t.start_seconds && (
                    <div className="text-xs text-muted-foreground">
                      {formatTimestamp(t.start_seconds)} – {formatTimestamp(t.end_seconds)}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.notable_quotes?.length > 0 && (
        <ExtrasCard title="Notable quotes" icon={Quote}>
          <ul className="space-y-3">
            {extras.notable_quotes.map((q, i) => (
              <li key={i} className="border-l-2 border-primary/40 pl-3">
                <p className="text-sm italic leading-relaxed text-foreground/85">"{q.quote}"</p>
                <div className="mt-1.5">
                  <TimestampPill url={youtubeTimestampUrl(video.youtube_url, q.timestamp_seconds)} seconds={q.timestamp_seconds} />
                </div>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.recommendations_mentioned?.length > 0 && (
        <ExtrasCard title="Recommendations" icon={HelpingHand}>
          <ul className="space-y-2.5">
            {extras.recommendations_mentioned.map((r, i) => (
              <li key={i} className="flex flex-wrap items-start gap-2">
                <Badge variant="outline" className="font-normal capitalize">{r.kind}</Badge>
                <span className="flex-1 text-sm text-foreground/85">{r.title}</span>
                <TimestampPill url={youtubeTimestampUrl(video.youtube_url, r.timestamp_seconds)} seconds={r.timestamp_seconds} />
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}
    </>
  )
}

// ---------------------------------------------------------------------
// NEWS ANALYSIS
// ---------------------------------------------------------------------

function NewsAnalysisBlock({ video, extras }: { video: VideoDetail; extras: NewsAnalysisExtras }) {
  return (
    <>
      {extras.thesis && (
        <ExtrasCard title="Thesis" icon={Compass}>
          <p className="text-base font-medium leading-relaxed text-foreground">{extras.thesis}</p>
          {extras.context && (
            <p className="text-sm leading-relaxed text-muted-foreground">{extras.context}</p>
          )}
        </ExtrasCard>
      )}

      {extras.key_claims?.length > 0 && (
        <ExtrasCard title="Key claims" icon={Flag}>
          <ul className="space-y-5">
            {extras.key_claims.map((c, i) => (
              <li key={i} className="space-y-2 border-l-2 border-primary/40 pl-4">
                <p className="text-sm font-medium leading-snug text-foreground">{c.claim}</p>
                {c.supporting_arguments?.length > 0 && (
                  <ul className="mt-2 space-y-2">
                    {c.supporting_arguments.map((arg, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <TimestampPill url={youtubeTimestampUrl(video.youtube_url, arg.timestamp_seconds)} seconds={arg.timestamp_seconds} />
                        <span className="flex-1 text-sm text-foreground/80">{arg.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.sources_cited?.length > 0 && (
        <ExtrasCard title="Sources cited" icon={LinkIcon}>
          <ul className="space-y-2.5">
            {extras.sources_cited.map((s, i) => (
              <li key={i} className="flex flex-wrap items-start gap-2">
                <span className="flex-1 text-sm text-foreground/80">{s.description}</span>
                <TimestampPill url={youtubeTimestampUrl(video.youtube_url, s.timestamp_seconds)} seconds={s.timestamp_seconds} />
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}

      {extras.broader_implications?.length > 0 && (
        <ExtrasCard title="Broader implications" icon={Globe}>
          <ul className="space-y-2">
            {extras.broader_implications.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" aria-hidden="true" />
                <span className="text-sm leading-relaxed text-foreground/85">{b}</span>
              </li>
            ))}
          </ul>
        </ExtrasCard>
      )}
    </>
  )
}

// ---------------------------------------------------------------------
// NEWS BRIEF
// ---------------------------------------------------------------------

function NewsBriefBlock({ video, extras }: { video: VideoDetail; extras: NewsBriefExtras }) {
  if (!extras.stories?.length) return null
  return (
    <ExtrasCard title="Stories" icon={Newspaper}>
      <ol className="space-y-4">
        {extras.stories.map((s, i) => (
          <li key={i} className="flex gap-3">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
              {i + 1}
            </div>
            <div className="flex-1 space-y-1">
              <div className="flex flex-wrap items-start gap-2">
                <span className="flex-1 text-sm font-semibold text-foreground">{s.headline}</span>
                <TimestampPill url={youtubeTimestampUrl(video.youtube_url, s.timestamp_seconds)} seconds={s.timestamp_seconds} />
              </div>
              {s.summary && (
                <p className="text-sm leading-relaxed text-foreground/75">{s.summary}</p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </ExtrasCard>
  )
}
